import { computed, onScopeDispose, ref, watch, type Ref } from 'vue';
import type { SortingState } from '@tanstack/vue-table';
import { drugUnitLabel, drugUnitValue } from '@/composables/inventory/inventoryTable';
import { useInventoryScope } from '@/composables/inventory/useInventoryScope';
import { weekInterval } from '@/composables/records/useRecordsAggregation';
import { i18n } from '@/plugins/internationalization/i18n';
import { useMedicineDiagnoses, useMedicineInventoryKeys, useMedicineMovements, useMedicineTreatmentWeeks } from '@/plugins/store/actions/queries/inventory';
import type { MedicineInventoryScope, MedicineMovementSort, MedicineMovementsParams, MedicineMovementType, MedicineTreatmentWeeksParams } from '@/types';

export type MovementsPageKey = { farmId: number; drugId: number; unitId: number };

export const DEFAULT_PAGE_SIZE = 25;

/** How long typing in the search must pause before it becomes a request. */
const SEARCH_DEBOUNCE_MS = 300;
export const PAGE_SIZE_OPTIONS = [25, 50, 100];

/** The sortable columns and the sort the endpoint names them by. */
const SORT_BY_COLUMN: Record<string, MedicineMovementSort> = { date: 'date', quantity: 'quantity', user_name: 'user' };

const MOVEMENT_TYPES: MedicineMovementType[] = ['purchase', 'treatment', 'retreatment', 'waste', 'count'];

/** What the treatments chart counts, and so what a week picked on it narrows the table to. */
const TREATMENT_TYPES: MedicineMovementType[] = ['treatment', 'retreatment'];

/** The type facet's label, resolved per render so a locale switch is picked up. */
export function movementTypeLabel(type: MedicineMovementType): string {
    return i18n.global.t(`views.health.inventory.movementTypes.${type}`);
}

/**
 * One movements table's data and view state, for either the Log page (the period's movements of every
 * selected farm, narrowed by type, farm, drug, diagnosis and search, plus its treatments chart) or a ledger
 * key's detail page (the key's whole history, whatever the period: one farm, drug and unit, fixed by the
 * route, narrowed by type and search).
 *
 * Filtering, sorting and paging are all the server's: every change below becomes a request parameter,
 * and any change other than the page itself returns the table to page one. The chart reads the same
 * farm, drug and diagnosis filters but never the type: it always counts treatments. A week picked on it
 * narrows the table to that week's treatments, and a new period clears it.
 */
export function useMovementsPage(key: Ref<MovementsPageKey | null>) {
    const { scope: pickerScope, isScopeEnabled: isPickerScopeEnabled, scopeOfFarm, periodRange } = useInventoryScope();
    const isLog = computed(() => key.value === null);

    const types = ref<string[]>([]);
    const farmFilter = ref<string[]>([]);
    const diagnosisFilter = ref<string[]>([]);
    const drugFilter = ref<string[]>([]);
    /** The search as typed, and as sent once typing pauses. */
    const search = ref('');
    const sentSearch = ref('');
    let searchTimer: ReturnType<typeof setTimeout> | undefined;

    watch(search, (value) => {
        clearTimeout(searchTimer);
        searchTimer = setTimeout(() => {
            sentSearch.value = value.trim();
        }, SEARCH_DEBOUNCE_MS);
    });
    onScopeDispose(() => clearTimeout(searchTimer));
    /** Monday of the week picked on the Log's chart, or null. */
    const selectedWeekStart = ref<string | null>(null);
    const sorting = ref<SortingState>([{ id: 'date', desc: true }]);
    const page = ref(1);
    const pageSize = ref(DEFAULT_PAGE_SIZE);

    /** The Log's farms (the farm filter, else every selected farm) over the picker's period: what its chart reads. */
    const logScope = computed<MedicineInventoryScope>(() => {
        const farmIds = farmFilter.value.map(Number);

        return { period: pickerScope.value.period, locationIds: farmIds.length ? farmIds : pickerScope.value.locationIds };
    });

    /** The picked week's days that fall inside the period, as the period query string the endpoints take. */
    function weekPeriod(weekStart: string): string {
        const period = new URLSearchParams(pickerScope.value.period);
        const [weekFrom, weekTo] = weekInterval(weekStart);
        const { from, to } = periodRange.value;

        period.set('from', from > weekFrom ? from : weekFrom);
        period.set('to', to !== '' && to < weekTo ? to : weekTo);

        return period.toString();
    }

    const scope = computed<MedicineInventoryScope>(() => {
        if (key.value !== null) {
            return scopeOfFarm(key.value.farmId);
        }

        return selectedWeekStart.value === null ? logScope.value : { ...logScope.value, period: weekPeriod(selectedWeekStart.value) };
    });

    /** The type filter as sent. With a week picked only treatment types are left, both when the filter names neither. */
    const requestTypes = computed<MedicineMovementType[]>(() => {
        const chosen = types.value.filter((type): type is MedicineMovementType => (MOVEMENT_TYPES as string[]).includes(type));

        if (!isLog.value || selectedWeekStart.value === null) {
            return chosen;
        }

        const treatments = chosen.filter((type) => TREATMENT_TYPES.includes(type));

        return treatments.length ? treatments : TREATMENT_TYPES;
    });

    const params = computed<MedicineMovementsParams>(() => {
        const [sort] = sorting.value;
        // A detail page's key, or the Log's drug filter
        const drug = key.value !== null ? { drugId: key.value.drugId, unitId: key.value.unitId } : { drugs: drugFilter.value };

        return {
            scope: scope.value,
            ...drug,
            allHistory: !isLog.value,
            types: requestTypes.value,
            diagnoses: diagnosisFilter.value,
            search: sentSearch.value,
            sort: (sort && SORT_BY_COLUMN[sort.id]) || 'date',
            descending: sort ? sort.desc : true,
            page: page.value,
            pageSize: pageSize.value,
        };
    });

    const isEnabled = computed(() => (isLog.value ? isPickerScopeEnabled.value : true));
    const { data, isFetching, isError, refetch } = useMedicineMovements(params, { enabled: isEnabled });

    const treatmentWeeksParams = computed<MedicineTreatmentWeeksParams>(() => ({ scope: logScope.value, drugs: drugFilter.value, diagnoses: diagnosisFilter.value }));
    const { data: treatmentWeeksData, isError: isTreatmentWeeksError } = useMedicineTreatmentWeeks(treatmentWeeksParams, {
        enabled: computed(() => isLog.value && isPickerScopeEnabled.value),
    });
    const treatmentWeeks = computed(() => treatmentWeeksData.value ?? []);
    /** Everything the chart's rows are fetched for (period, farms, drugs, diagnoses) as one string, so the chart can tell when its scope changed. */
    const treatmentWeeksKey = computed(() => JSON.stringify(treatmentWeeksParams.value));

    function selectWeek(weekStart: string) {
        selectedWeekStart.value = selectedWeekStart.value === weekStart ? null : weekStart;
    }

    function resetWeek() {
        selectedWeekStart.value = null;
    }

    // A week of another period means nothing in this one.
    watch(
        () => pickerScope.value.period,
        () => resetWeek()
    );

    // The Log's farm and drug choices come from the keys of the scope: names only, no ledger to compute.
    const keyScopeIds = computed(() => pickerScope.value.locationIds);
    const { data: ledgerKeys } = useMedicineInventoryKeys(keyScopeIds, { enabled: computed(() => isLog.value && isPickerScopeEnabled.value) });

    // The diagnoses on offer are those of the instructions on the farms in scope, whatever the Log is narrowed to.
    const diagnosisScopeIds = computed(() => pickerScope.value.locationIds);
    const { data: diagnoses } = useMedicineDiagnoses(diagnosisScopeIds, { enabled: computed(() => isLog.value && isPickerScopeEnabled.value) });
    const diagnosisOptions = computed(() => (diagnoses.value ?? []).map((diagnosis) => ({ value: diagnosis, label: diagnosis })));

    const typeOptions = computed(() => MOVEMENT_TYPES.map((type) => ({ value: type, label: movementTypeLabel(type) })));
    const farmOptions = computed(() => {
        const farms = new Map<string, string>();

        for (const row of ledgerKeys.value ?? []) {
            farms.set(String(row.farm_id), row.farm_name);
        }

        return [...farms].map(([value, label]) => ({ value, label })).sort((a, b) => a.label.localeCompare(b.label));
    });
    const drugOptions = computed(() => {
        const drugs = new Map<string, string>();

        for (const row of ledgerKeys.value ?? []) {
            drugs.set(drugUnitValue(row), drugUnitLabel(row));
        }

        return [...drugs].map(([value, label]) => ({ value, label })).sort((a, b) => a.label.localeCompare(b.label));
    });

    /**
     * Net movement of the one drug the Log is narrowed to: the endpoint sums the signed quantity of every
     * row the filters leave, so the figure always matches the rows listed, whatever the type filter. Units
     * do not add up, so with several drugs picked there is no such figure.
     */
    const netMovement = computed(() => {
        const netQuantity = data.value?.netQuantity ?? null;

        if (!isLog.value || drugFilter.value.length !== 1 || netQuantity === null) {
            return null;
        }

        const drug = (ledgerKeys.value ?? []).find((row) => drugUnitValue(row) === drugFilter.value[0]);

        return drug ? { quantity: netQuantity, unitName: drug.unit_name } : null;
    });

    // Anything but the page itself changing makes the current page meaningless.
    watch([types, farmFilter, diagnosisFilter, drugFilter, sentSearch, sorting, scope, pageSize], () => {
        page.value = 1;
    });

    const rows = computed(() => data.value?.rows ?? []);
    const totalRows = computed(() => data.value?.totalRows ?? 0);
    const totalPages = computed(() => data.value?.totalPages ?? 1);

    /**
     * Consumption per diagnosis, offered while the Log shows treatments alone and they span more than one
     * drug: with a single drug the rows already say it all.
     */
    const diagnosisTotals = computed(() => {
        const totalsByDiagnosis = data.value?.diagnosisTotals ?? null;
        const isTreatmentsOnly = requestTypes.value.length > 0 && requestTypes.value.every((type) => TREATMENT_TYPES.includes(type));

        if (!isLog.value || !isTreatmentsOnly || totalsByDiagnosis === null) {
            return null;
        }

        const drugNames = new Set(totalsByDiagnosis.flatMap((total) => total.drug_names));

        return drugNames.size > 1 ? totalsByDiagnosis : null;
    });

    return {
        diagnosisFilter,
        diagnosisOptions,
        diagnosisTotals,
        drugFilter,
        drugOptions,
        farmFilter,
        farmOptions,
        isError,
        isFetching,
        page,
        pageSize,
        refetch,
        resetWeek,
        rows,
        search,
        selectWeek,
        selectedWeekStart,
        sorting,
        totalPages,
        totalRows,
        netMovement,
        treatmentWeeks,
        treatmentWeeksKey,
        isTreatmentWeeksError,
        typeOptions,
        types,
    };
}
